/**
 * Layout de la consola de la plataforma (SuperAdmin) con la identidad de Finance360:
 * el mismo armazón que la consola de las cuentas (estilos/finance.css) con el menú en
 * índigo sólido, como el panel de la plataforma de Payroll360.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  LayoutDashboard, Building2, AlertTriangle, DatabaseBackup, ScrollText, UserCog, LogOut, Menu, X, BellRing, Palette, Tags,
  Blocks
} from 'lucide-react';
import useSuperadminAuthStore from '../store/authStore';

const navegacion = [
  { name: 'Resumen', href: '/superadmin', icon: LayoutDashboard, exacto: true },
  { name: 'Cuentas', href: '/superadmin/cuentas', icon: Building2 },
  { name: 'Planes', href: '/superadmin/planes', icon: Tags },
  { name: 'Módulos Avanzados', href: '/superadmin/modulos-avanzados', icon: Blocks },
  { name: 'Rechazos de Hacienda', href: '/superadmin/rechazos', icon: AlertTriangle },
  { name: 'Alertas por correo', href: '/superadmin/alertas', icon: BellRing },
  { name: 'Respaldos', href: '/superadmin/respaldos', icon: DatabaseBackup },
  { name: 'Apariencia', href: '/superadmin/apariencia', icon: Palette },
  { name: 'Auditoría', href: '/superadmin/audit', icon: ScrollText },
  { name: 'Mi cuenta', href: '/superadmin/settings', icon: UserCog }
];

export default function SuperadminLayout({ children }) {
  const [abierto, setAbierto] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useSuperadminAuthStore();

  const activo = (item) => item.exacto ? location.pathname === item.href : location.pathname.startsWith(item.href);
  const pantalla = navegacion.find(activo)?.name || 'Consola';

  const salir = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  const Lateral = () => (
    <div className="flex flex-col h-full">
      <div className="f360-regla" />
      <div className="f360-marca flex flex-col items-center px-3 pt-5 pb-4 relative">
        <Link to="/superadmin" aria-label="Ir al resumen">
          <img src="/marca/f360-logo-blanco-2026.svg" alt="Finance360 by Kuanta Bridge" />
        </Link>
        <span className="f360-rotulo mt-2">Consola de la plataforma</span>
        <button onClick={() => setAbierto(false)} className="lg:hidden absolute right-3 top-4 p-1 rounded" aria-label="Cerrar menú">
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="f360-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto border-t border-white/10">
        {navegacion.map(item => (
          <Link key={item.href} to={item.href} onClick={() => setAbierto(false)} className={clsx(activo(item) && 'on')}>
            <item.icon />{item.name}
          </Link>
        ))}
      </nav>
      <div className="f360-pie p-3 border-t">
        <div className="f360-usuario flex items-center gap-3 px-2 py-2">
          <div className="avatar h-9 w-9 rounded-full flex items-center justify-center shrink-0"><UserCog className="h-4 w-4" /></div>
          <div className="min-w-0">
            <p className="nombre text-sm font-semibold truncate">{user?.name || 'SuperAdmin'}</p>
            <p className="correo text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={salir} className="f360-salir mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm">
          <LogOut className="h-4 w-4" />Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="f360 min-h-screen">
      {abierto && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setAbierto(false)} />}
      <aside className={clsx('f360-lateral fixed top-0 left-0 z-50 h-full w-72 transition-transform lg:translate-x-0',
        abierto ? 'translate-x-0' : '-translate-x-full')}>
        <Lateral />
      </aside>
      <div className="lg:pl-72">
        <header className="f360-cabecera sticky top-0 z-30 h-16 px-4 lg:px-8 flex items-center gap-3">
          <button onClick={() => setAbierto(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100" aria-label="Abrir menú"><Menu className="h-5 w-5" /></button>
          <span className="f360-ojo">Finance 360 · Plataforma</span>
          <span className="text-gray-300">/</span>
          <span className="f360-titulo">{pantalla}</span>
        </header>
        <main className="p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
